import request from '../utils/request';
import { ContentChannel, ContentItem, ContentItemPayload, PublicContentResponse } from '../types/content';

const BASE = '/api/v1/content';

export interface ListParams {
  page?: number;
  page_size?: number;
  channel?: number;
  status?: string;
  keyword?: string;
}

export async function listContentChannels(): Promise<ContentChannel[]> {
  const res = await request.get(`${BASE}/channels/`);
  const data = res.data;
  if (Array.isArray(data)) return data;
  return data?.results || [];
}

export async function listContentItems(params: ListParams): Promise<{ count: number; results: ContentItem[] }> {
  const res = await request.get(`${BASE}/items/`, { params });
  const data = res.data || {};
  return {
    count: data.count || 0,
    results: data.results || [],
  };
}

export async function getContentItem(id: number): Promise<ContentItem> {
  const res = await request.get(`${BASE}/items/${id}/`);
  return res.data;
}

export async function createContentItem(payload: ContentItemPayload): Promise<ContentItem> {
  const res = await request.post(`${BASE}/items/`, payload);
  return res.data;
}

export async function updateContentItem(id: number, payload: Partial<ContentItemPayload>): Promise<ContentItem> {
  const res = await request.patch(`${BASE}/items/${id}/`, payload);
  return res.data;
}

export async function deleteContentItem(id: number): Promise<void> {
  await request.delete(`${BASE}/items/${id}/`);
}

export async function publishContentItem(id: number): Promise<ContentItem> {
  const res = await request.post(`${BASE}/items/${id}/publish/`);
  return res.data;
}

export async function unpublishContentItem(id: number): Promise<ContentItem> {
  const res = await request.post(`${BASE}/items/${id}/unpublish/`);
  return res.data;
}

export async function batchUpdateContentItemOrder(orders: Array<{ id: number; sort_order: number }>): Promise<void> {
  await request.post(`${BASE}/items/batch-update-order/`, { orders });
}

export async function getPublicChannelItems(channelKey: string): Promise<PublicContentResponse> {
  const res = await request.get(`${BASE}/public/${channelKey}/items/`);
  return res.data || { count: 0, results: [] };
}

export async function listPublicAnnouncements(params?: {
  page?: number;
  page_size?: number;
}): Promise<{ count: number; next?: string | null; previous?: string | null; results: ContentItem[] }> {
  const res = await request.get(`${BASE}/public/announcements/`, { params });
  const data = res.data || {};
  return {
    count: data.count || 0,
    next: data.next || null,
    previous: data.previous || null,
    results: data.results || [],
  };
}

export interface PublicAnnouncementDetailResponse {
  item: ContentItem;
  prev: { id: number; title: string } | null;
  next: { id: number; title: string } | null;
}

export async function getPublicAnnouncementDetail(id: number): Promise<PublicAnnouncementDetailResponse> {
  const res = await request.get(`${BASE}/public/items/${id}/`);
  return res.data;
}

export interface PublicGeneralContentDetailResponse {
  item: ContentItem;
  prev: { slug: string; title: string } | null;
  next: { slug: string; title: string } | null;
}

export async function listPublicGeneralContents(params?: {
  page?: number;
  page_size?: number;
}): Promise<{ count: number; next?: string | null; previous?: string | null; results: ContentItem[] }> {
  const res = await request.get(`${BASE}/public/general-contents/`, { params });
  const data = res.data || {};
  return {
    count: data.count || 0,
    next: data.next || null,
    previous: data.previous || null,
    results: data.results || [],
  };
}

export async function getPublicGeneralContentDetail(slug: string): Promise<PublicGeneralContentDetailResponse> {
  const res = await request.get(`${BASE}/public/general/${encodeURIComponent(slug)}/`);
  return res.data;
}

export async function uploadContentImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await request.post<{ url: string }>(`${BASE}/upload-image/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const rawUrl = (res.data?.url || '').trim();
  if (!rawUrl) return '';
  if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  return new URL(rawUrl, apiBase).toString();
}

export async function uploadContentAttachment(
  file: File
): Promise<{ url: string; file_name: string; file_size: number }> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await request.post<{ url: string; file_name: string; file_size: number }>(
    `${BASE}/upload-attachment/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  const rawUrl = (res.data?.url || '').trim();
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  const url = !rawUrl ? '' : /^https?:\/\//i.test(rawUrl) ? rawUrl : new URL(rawUrl, apiBase).toString();
  return { url, file_name: res.data?.file_name || file.name, file_size: res.data?.file_size ?? file.size };
}

export interface ResourceHubNav {
  slug: string;
  title: string;
}

export interface ResourceHubListResponse {
  channel?: { key: string; name: string };
  count: number;
  next?: string | null;
  previous?: string | null;
  results: ContentItem[];
}

export async function listPublicResourceItems(
  channelKey: string,
  params?: { page?: number; page_size?: number }
): Promise<ResourceHubListResponse> {
  const res = await request.get(`${BASE}/public/resource/${channelKey}/items/`, { params });
  const data = res.data || {};
  return {
    channel: data.channel,
    count: data.count || 0,
    next: data.next || null,
    previous: data.previous || null,
    results: data.results || [],
  };
}

export interface ResourceHubDetailResponse {
  channel?: { key: string; name: string };
  item: ContentItem;
  prev: ResourceHubNav | null;
  next: ResourceHubNav | null;
}

export async function getPublicResourceItemDetail(
  channelKey: string,
  slug: string
): Promise<ResourceHubDetailResponse> {
  const res = await request.get(`${BASE}/public/resource/${channelKey}/items/${encodeURIComponent(slug)}/`);
  return res.data;
}

export interface ResourceHubFeedResponse {
  channels: Array<{ key: string; name: string }>;
  count: number;
  next?: string | null;
  previous?: string | null;
  results: ContentItem[];
}

export async function getPublicResourceHubFeed(
  params?: { page?: number; page_size?: number }
): Promise<ResourceHubFeedResponse> {
  const res = await request.get(`${BASE}/public/resource-hub/feed/`, { params });
  const data = res.data || {};
  return {
    channels: data.channels || [],
    count: data.count || 0,
    next: data.next || null,
    previous: data.previous || null,
    results: data.results || [],
  };
}

export interface PublicContactFormPayload {
  name: string;
  email: string;
  subject: string;
  message: string;
  company?: string;
  country?: string;
  phone?: string;
}

export async function submitPublicContactForm(payload: PublicContactFormPayload): Promise<{ detail: string }> {
  const res = await request.post(`${BASE}/public/contact/submit/`, payload);
  return res.data || { detail: '提交成功，我们会尽快联系您' };
}

